import React from 'react';
import { Typography, Box, FormControl, Select, MenuItem, Fab, Chip } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../../constants/icons';
import { STANDARD_FAB_CTA_SX } from '../../constants/buttonStyles';
import { generateFiscalYearOptions } from '../profitAndLoss/constants';
import type { UseRecruitmentPlanResult } from '../../hooks/useRecruitmentPlan';

type Props = {
  plan: UseRecruitmentPlanResult;
  onSave: () => void;
};

/**
 * 人事採用 計画入力ページの見出し＋絞り込みヘッダー。
 * 会計年度・予算バージョンのセレクトと保存ボタンを常時表示する。
 */
export default function RecruitmentPlanFilterHeader({ plan, onSave }: Props) {
  const { fiscalYear, setFiscalYear, budgetVersions, versionCode, setVersionCode, saving, dirty } = plan;
  const fiscalYearOptions = generateFiscalYearOptions();

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[500] }}>
          <Icons.Groups /> 人事採用 計画入力
        </Typography>
        <Typography variant="body2" sx={{ color: '#475569' }}>
          拠点・部門・役職別に、既存在籍数へ月次の採用予定 (+) を積み上げて計画在籍数 (増員計画) を作成します
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {/* 会計年度 */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={fiscalYear}
            onChange={(e) => setFiscalYear(Number(e.target.value))}
            sx={{ fontSize: '13px', backgroundColor: '#fff', '& .MuiSelect-select': { py: 0.75 } }}
          >
            {fiscalYearOptions.map((o) => (
              <MenuItem key={o.value} value={o.value} sx={{ fontSize: '13px' }}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 予算バージョン */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={budgetVersions.length > 0 ? versionCode : ''}
            displayEmpty
            onChange={(e) => setVersionCode(String(e.target.value))}
            sx={{ fontSize: '13px', backgroundColor: '#fff', '& .MuiSelect-select': { py: 0.75 } }}
          >
            {budgetVersions.length === 0 && (
              <MenuItem value="" sx={{ fontSize: '13px' }}>
                バージョン未取得
              </MenuItem>
            )}
            {budgetVersions.map((v) => (
              <MenuItem key={v.version_code} value={v.version_code} sx={{ fontSize: '13px' }}>
                {v.version_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {dirty && (
          <Chip size="small" label="未保存" sx={{ fontSize: '11px', height: '22px', bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }} />
        )}

        {/* 保存 */}
        <Fab
          variant="extended"
          size="small"
          disabled={saving || !versionCode}
          onClick={onSave}
          sx={STANDARD_FAB_CTA_SX}
        >
          <Icons.Save />
          {saving ? '保存中...' : '計画を保存'}
        </Fab>
      </Box>
    </Box>
  );
}
